import { useAuthentication } from 'src/features/authentication/context';
import { Protected } from 'src/features/protected-route/components';
import { ACCOUNT_ISSUE_PATH, EDIT_GATEWAY_V2_HEADER, LOGIN_PATH } from '../../config';
import { AppLayout } from 'src/layouts';
import { NAV_ITEMS_HEADER_SELECTED_SITE, getNavItemsHeaderSelectedSiteSAAndAA } from 'src/features';
import { ElementProps } from '../../types/element';
import { LoadingElement } from '../LoadingElement';
import { EditGatewayV2Page } from 'src/routes/pages';
import { AccessRoles } from 'src/features/user-account-details/types';

/**
 * Guard/layout wrapper for the v2 gateway edit page (/gateways/v2/:gatewayId).
 * Copy of GatewayEditElement — kept separate so v1 stays untouched.
 * See src/features/gateway-details/EditGatewayV2.md.
 */
export const GatewayEditV2Element = ({ isSuperAdmin }: ElementProps) => {
  const {
    isCustomerAccessAllowed,
    isSiteAccessAllowed,
    loggedIn,
    customerId,
    siteId,
    siteIdBelongsToCustomer,
    getCurrentRole,
    user,
  } = useAuthentication();

  const isAccountAdmin =
    getCurrentRole(customerId.value, siteId.value) === AccessRoles.ACCOUNT_ADMIN;

  const getUniqueRedirectPath = () => {
    if (!loggedIn.value) return LOGIN_PATH;
    if (!isSuperAdmin && (!isCustomerAccessAllowed.value || !isSiteAccessAllowed.value))
      return ACCOUNT_ISSUE_PATH + '?reason=Access Denied';
    if (!siteIdBelongsToCustomer.value) return ACCOUNT_ISSUE_PATH + '?reason=Site does not exist';
  };
  const GatewayEditV2RouteElement = () => (
    <Protected
      isAuthenticated={loggedIn.value}
      isAuthorized={
        (isSuperAdmin || (isCustomerAccessAllowed.value && isSiteAccessAllowed.value)) &&
        siteIdBelongsToCustomer.value
      }
      uniqueRedirectPath={getUniqueRedirectPath()}
    >
      <AppLayout
        isSiteHeader
        headerTitle={EDIT_GATEWAY_V2_HEADER}
        headerNavItems={
          isSuperAdmin || isAccountAdmin
            ? getNavItemsHeaderSelectedSiteSAAndAA(user, isSuperAdmin)
            : NAV_ITEMS_HEADER_SELECTED_SITE
        }
      >
        <EditGatewayV2Page />
      </AppLayout>
    </Protected>
  );

  const getGatewayEditV2Element = () => {
    if (!loggedIn.loaded) return <LoadingElement />;

    if (loggedIn.loaded && !loggedIn.value) return <GatewayEditV2RouteElement />;

    if (!customerId.value || !customerId.loaded || !siteId.value || !siteId.loaded)
      return <LoadingElement />;

    if (!siteIdBelongsToCustomer.loaded) return <LoadingElement />;

    if (isSuperAdmin && loggedIn.loaded) return <GatewayEditV2RouteElement />;

    if (!isCustomerAccessAllowed.loaded || !isSiteAccessAllowed.loaded) return <LoadingElement />;

    return <GatewayEditV2RouteElement />;
  };

  return getGatewayEditV2Element();
};
